// In the Together Android TWA the web layer cannot read the real OS notification state: `Notification.permission`
// reflects Chrome's per-origin web permission, which never syncs with the native app's POST_NOTIFICATIONS grant
// (the app requests that natively, not through Chrome). So the native app is the source of truth and passes the
// real state to the web as a `notificationsEnabled` query param on the launch URL (see discovery-together-twa
// LauncherActivity / UrlHandlerActivity). We capture it once at startup — before the SPA router strips the query
// string — and stash it where the Notifications page can read it after client-side navigation.

const PARAM = 'notificationsEnabled'
const STORAGE_KEY = 'twaNotificationsEnabled'
const PROMPTED_KEY = 'twaNotificationsPrompted'
const SEEDED_KEY = 'notificationDefaultsSeeded'

/**
 * Reads the `notificationsEnabled` launch param (if present) and persists it for the session.
 * Call as early as possible on app startup.
 */
export function captureTwaNotificationState() {
  if (typeof window === 'undefined') return
  try {
    const params = new URLSearchParams(window.location.search)
    if (!params.has(PARAM)) return
    window.sessionStorage.setItem(STORAGE_KEY, params.get(PARAM) === 'true' ? 'true' : 'false')
  } catch {
    // sessionStorage/URL parsing can throw in locked-down webviews; treat as "unknown".
  }
}

/**
 * The native-reported notification-enabled state for this session.
 * @returns {boolean | null} true/false when the native app reported it, null when unknown.
 */
export function getTwaNotificationsEnabled() {
  if (typeof window === 'undefined') return null
  try {
    const value = window.sessionStorage.getItem(STORAGE_KEY)
    if (value === 'true') return true
    if (value === 'false') return false
  } catch {
    // ignore
  }
  return null
}

/**
 * Whether we've already triggered the native notification-permission prompt. Persisted across launches
 * (localStorage, not sessionStorage) so we only auto-prompt ONCE — otherwise every cold launch re-fires
 * `togetherscheme://request-notifications`, and once the OS permission is permanently denied (USER_FIXED)
 * that request is a no-op that collapses the TWA task. In the TWA `Notification.permission` can't tell us
 * we've already asked (it never leaves 'default'), hence this explicit flag.
 */
export function hasPromptedTwaNotifications() {
  if (typeof window === 'undefined') return false
  try {
    return window.localStorage.getItem(PROMPTED_KEY) === '1'
  } catch {
    return false
  }
}

export function markTwaNotificationsPrompted() {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(PROMPTED_KEY, '1')
  } catch {
    // ignore
  }
}

// Seeding writes a specific account's server prefs, so the "already seeded" flag must be per-account, not
// per-device — otherwise the first account to log in on a device sets a shared flag that starves every
// other account (they'd never get their defaults seeded). Keyed by CIS ID.
const seededKeyFor = (userId) => `${SEEDED_KEY}:${userId}`

/**
 * Whether we've already seeded THIS user's default push subscriptions (the one-time `POST /prefs/initialize`
 * on first consent). Persisted per-account (localStorage, keyed by CIS ID) so each account is seeded at
 * most once and we never re-seed one — which would overwrite that user's later customizations.
 */
export function hasSeededNotificationDefaults(userId) {
  if (typeof window === 'undefined' || !userId) return false
  try {
    return window.localStorage.getItem(seededKeyFor(userId)) === '1'
  } catch {
    return false
  }
}

export function markSeededNotificationDefaults(userId) {
  if (typeof window === 'undefined' || !userId) return
  try {
    window.localStorage.setItem(seededKeyFor(userId), '1')
  } catch {
    // ignore
  }
}

const FROM_PARAM = 'from'
const FROM_NOTIFICATION_VALUE = 'notification'
const OPENED_FROM_NOTIFICATION_KEY = 'twaOpenedFromDiscoveryCodeNotification'

/**
 * Reads the `?from=notification` launch param (set by the Discovery Code notification tap) and persists it
 * for the session. Call as early as possible on app startup.
 */
export function captureDiscoveryCodeLaunchParams() {
  if (typeof window === 'undefined') return
  try {
    const params = new URLSearchParams(window.location.search)
    if (params.get(FROM_PARAM) !== FROM_NOTIFICATION_VALUE) return
    window.sessionStorage.setItem(OPENED_FROM_NOTIFICATION_KEY, 'true')
  } catch {
    // ignore
  }
}

/**
 * Whether this launch was opened by tapping the Discovery Code notification. Clears the flag when it
 * reports true.
 * @returns {boolean}
 */
export function wasOpenedFromDiscoveryCodeNotification() {
  if (typeof window === 'undefined') return false
  try {
    if (window.sessionStorage.getItem(OPENED_FROM_NOTIFICATION_KEY) !== 'true') return false
    window.sessionStorage.removeItem(OPENED_FROM_NOTIFICATION_KEY)
    return true
  } catch {
    return false
  }
}

const LIVE_ACTIVITY_UNAVAILABLE_FIRED_KEY = 'twaLiveActivityUnavailableFired'

export function hasFiredLiveActivityUnavailable() {
  if (typeof window === 'undefined') return false
  try {
    return window.sessionStorage.getItem(LIVE_ACTIVITY_UNAVAILABLE_FIRED_KEY) === 'true'
  } catch {
    return false
  }
}

export function markLiveActivityUnavailableFired() {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(LIVE_ACTIVITY_UNAVAILABLE_FIRED_KEY, 'true')
  } catch {
    // ignore
  }
}
